/**
 * FriendProfileModal — Roblox-style slide-over showing a friend's profile:
 * avatar, @username, online status, Level/Stars/Streak, best scores, total
 * friends + mutual friends, and quick actions (Message, Invite to Play, and
 * relationship actions). Opened globally via FriendProfileHost.
 */
import React, { useState, useEffect, useCallback } from "react";
import { X, Star, Zap, Users, MessageCircle, Gamepad2, UserPlus, Ban, Loader2, Radio } from "lucide-react";
import axios from "axios";
import { getStickerEmoji } from "./StickerPicker";
import FriendFace from "./FriendFace";
import { TierChip } from "./TierBadge";

const API = process.env.REACT_APP_BACKEND_URL;
const glyph = (c) => getStickerEmoji(c?.sticker) || (c?.display_name || c?.username || "?")[0]?.toUpperCase() || "?";

const Stat = ({ icon: Icon, color, value, label }) => (
  <div className="flex flex-col items-center gap-1 flex-1">
    <Icon size={18} style={{ color }} />
    <span className="text-white font-bold text-base leading-none">{value}</span>
    <span className="text-white/40 text-[10px]">{label}</span>
  </div>
);

const FriendProfileModal = ({ viewerId, targetId, onClose, onMessage, onInvite, onTalk }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Retry transient failures (cold backend / brief blips) before giving up,
    // so a momentary hiccup doesn't strand the user on "Couldn't load".
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const r = await axios.get(`${API}/api/friends/${viewerId}/profile/${targetId}`);
        setP(r.data);
        setLoading(false);
        return;
      } catch {
        if (attempt < 3) await new Promise((res) => setTimeout(res, 600 * (attempt + 1)));
      }
    }
    setP(null);
    setLoading(false);
  }, [viewerId, targetId]);

  useEffect(() => { load(); }, [load]);

  const addFriend = async () => {
    try {
      await axios.post(`${API}/api/friends/request`, {
        from_user_id: viewerId, from_username: "", to_user_id: targetId, to_username: p?.username || p?.display_name,
      });
      load();
    } catch { /* silent */ }
  };

  return (
    <div className="fixed inset-0 z-[9997] flex justify-end" data-testid="friend-profile-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="friend-profile-backdrop" />
      <div className="relative w-full sm:w-[400px] h-full bg-slate-900 border-l-2 border-white/10 shadow-2xl overflow-y-auto animate-[slideIn_.25s_ease]"
        style={{ animationName: "none" }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 sticky top-0 bg-slate-900 z-10">
          <h3 className="text-white text-sm font-fredoka font-bold">Profile</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/5 text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center" data-testid="friend-profile-close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-violet-400" size={28} /></div>
        ) : !p ? (
          <div className="text-center py-16 text-white/50 text-sm flex flex-col items-center gap-3" data-testid="profile-load-failed">
            <span>Couldn't load this profile.</span>
            <button onClick={load} className="px-4 py-2 rounded-lg bg-violet-500/25 text-violet-100 text-xs font-bold hover:bg-violet-500/40 transition" data-testid="profile-retry">Try again</button>
          </div>
        ) : (
          <div className="p-5">
            {/* Identity */}
            <div className="flex flex-col items-center text-center">
              <button
                type="button"
                onClick={() => setZoom(true)}
                className="relative inline-flex focus:outline-none hover:scale-[1.04] transition-transform"
                title="Tap to enlarge"
                data-testid="friend-profile-avatar-zoom"
              >
                <FriendFace friend={p} className="w-20 h-20 rounded-full flex items-center justify-center text-4xl border-2 border-white/15 overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} />
                <span className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-slate-900 ${p.online ? "bg-emerald-400" : "bg-slate-500"}`} data-testid="friend-profile-presence" />
              </button>
              <h2 className="text-white text-xl font-fredoka font-bold mt-3">{p.display_name || p.username}</h2>
              {p.username && <p className="text-violet-300/60 text-xs">@{p.username}</p>}
              <div className="mt-1.5"><TierChip friend={p} testId="friend-profile-tier" /></div>
              <p className={`text-xs font-bold mt-1 ${p.online ? "text-emerald-400" : "text-white/40"}`}>{p.online ? "● Online now" : "Offline"}</p>
              <p className="text-white/50 text-xs mt-0.5">{p.level_name} · Level {p.level}</p>
            </div>

            {/* Stats */}
            <div className="flex items-stretch gap-2 mt-5 bg-white/5 rounded-2xl py-3 px-2 border border-white/10">
              <Stat icon={Star} color="#FBBF24" value={p.stars} label="Stars" />
              <div className="w-px bg-white/10" />
              <Stat icon={Zap} color="#F97316" value={`${p.streak}d`} label="Streak" />
              <div className="w-px bg-white/10" />
              <Stat icon={Users} color="#8B5CF6" value={p.friends_count} label="Friends" />
              <div className="w-px bg-white/10" />
              <Stat icon={Users} color="#22D3EE" value={p.mutual_count} label="Mutual" />
            </div>

            {/* Mutual friends preview */}
            {p.mutual?.length > 0 && (
              <div className="mt-4">
                <p className="text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2">Mutual friends</p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {p.mutual.map((m) => (
                    <div key={m.id} className="flex flex-col items-center w-[54px] shrink-0" title={m.display_name}>
                      <FriendFace friend={m} className="w-10 h-10 rounded-full flex items-center justify-center text-xl border border-white/15" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} />
                      <span className="text-white/50 text-[9px] truncate w-full text-center mt-0.5">{m.display_name || m.username}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best scores */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[["Memory", p.best_scores?.memory_match], ["Words", p.best_scores?.word_explorer], ["Math", p.best_scores?.math_quest]].map(([k, v]) => (
                <div key={k} className="bg-white/5 rounded-xl py-2 text-center border border-white/10">
                  <p className="text-white font-bold text-sm">{v || 0}</p>
                  <p className="text-white/40 text-[10px]">{k}</p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="mt-6 space-y-2">
              {p.state === "friends" ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => onMessage(targetId)} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-500/20 text-cyan-300 font-bold text-sm hover:bg-cyan-500/30 transition-all" data-testid="friend-profile-message">
                      <MessageCircle size={16} /> Message
                    </button>
                    <button onClick={() => { axios.post(`${API}/api/friends/ring`, { from_user_id: viewerId, to_user_id: targetId }).catch(() => {}); onTalk(targetId, p.display_name || p.username); }} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-fuchsia-500/20 text-fuchsia-300 font-bold text-sm hover:bg-fuchsia-500/30 transition-all" data-testid="friend-profile-talk">
                      <Radio size={16} /> Talk
                    </button>
                  </div>
                  <button onClick={() => onInvite(targetId)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-violet-500/20 text-violet-300 font-bold text-sm hover:bg-violet-500/30 transition-all" data-testid="friend-profile-invite">
                    <Gamepad2 size={16} /> Invite to Play
                  </button>
                </>
              ) : p.state === "none" ? (
                <button onClick={addFriend} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500/20 text-emerald-300 font-bold text-sm hover:bg-emerald-500/30 transition-all" data-testid="friend-profile-add">
                  <UserPlus size={16} /> Add Friend
                </button>
              ) : p.state === "request_sent" ? (
                <div className="w-full text-center py-3 rounded-xl bg-white/5 text-white/40 font-bold text-sm">Request sent</div>
              ) : p.state === "respond" ? (
                <button onClick={() => onMessage(null, "respond")} className="w-full py-3 rounded-xl bg-amber-500/20 text-amber-300 font-bold text-sm" data-testid="friend-profile-respond">Respond to request</button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {zoom && p && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-6"
          onClick={() => setZoom(false)}
          data-testid="friend-profile-avatar-zoom-overlay"
        >
          <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <FriendFace
              friend={p}
              className="w-72 h-72 max-w-[80vw] max-h-[80vw] rounded-3xl flex items-center justify-center text-[120px] border-2 border-white/15 overflow-hidden shadow-2xl"
              style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }}
            />
            <p className="text-white font-fredoka font-bold text-lg mt-4">{p.display_name || p.username}</p>
            <button
              onClick={() => setZoom(false)}
              className="mt-4 px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-bold flex items-center gap-1.5"
              data-testid="friend-profile-avatar-zoom-close"
            >
              <X size={15} /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendProfileModal;
